import type { SiteOnboardingDraft } from './onboarding-types';

export interface GenerationStreamRequest {
  businessName: string;
  industry: string;
  description: string;
  services: string;
  phone: string;
  siteId?: string;
  siteType?: SiteOnboardingDraft['siteType'];
  designConfig?: { mode: string; template?: string | null };
  vibeParams?: { tone: number; style: number };
  migrationConfig?: {
    targetUrl: string;
    useContent: boolean;
    useImages: boolean;
    cloneDesign: boolean;
  };
  customUrls?: string[];
  fullPromptOverride?: string;
  pagesConfig?: Array<{
    title: string;
    type: string;
    description: string;
    attachments: string[];
    verbosity: 'short' | 'medium' | 'long';
  }>;
  useNeutralImages?: boolean;
  /** Mapped to landing-editor `usePlaceholderImages` at POST boundary. */
  usePlaceholderImages?: boolean;
  generationModelId?: string;
  imageModelId?: string;
}

/** Map onboarding draft → POST `/api/generate-stream` body (landing-editor contract). */
export function draftToGenerationRequest(
  draft: SiteOnboardingDraft,
  options?: { siteId?: string; promptOverride?: string },
): GenerationStreamRequest {
  const description =
    draft.description.trim() ||
    [draft.mission, draft.vision].filter(Boolean).join('\n\n').trim();

  const migrationUrl = draft.migrationTargetUrl.trim();
  const pagesConfig =
    draft.siteType === 'multi' || draft.siteType === 'store'
      ? draft.pagesConfig.map((p) => ({
          title: p.title,
          type: p.type,
          description: p.description,
          attachments: p.attachments,
          verbosity: p.verbosity,
        }))
      : undefined;

  return {
    businessName: draft.businessName.trim() || 'New Site',
    industry: draft.industry.trim() || 'General',
    description,
    services: draft.services.trim(),
    phone: draft.phone.trim(),
    siteId: options?.siteId,
    siteType: draft.siteType,
    designConfig: { mode: 'custom' },
    vibeParams: {
      tone: draft.vibeTone,
      style: draft.vibeStyle,
    },
    migrationConfig: migrationUrl
      ? {
          targetUrl: migrationUrl,
          useContent: draft.migrationUseContent,
          useImages: draft.migrationUseImages,
          cloneDesign: draft.migrationCloneDesign,
        }
      : undefined,
    customUrls: draft.customUrls,
    fullPromptOverride: options?.promptOverride,
    pagesConfig,
    usePlaceholderImages: draft.useNeutralImages,
    generationModelId: draft.generationModelId,
    imageModelId: draft.imageModelId,
  };
}
